import { once } from 'ramda';
import { MutableRefObject, useEffect, useRef } from 'react';
import { validateGeolocation } from 'js/hooks/auth/pas';
import { htcmdService } from 'js/services/htcmd.service';

import * as Types from '../types';
import * as constants from '../constants';

export const useScheduler = ({
    sdk,
    nextTime,
    transactionID,
    userId,
    setTransactionID,
    setNextTime,
    triggerGeolocation,
    triggerTimestampRef,
    requestPacket,
    geolocationType,
    intervalPings,
    inactiveDetection
}: {
    sdk: Types.GeofencingSdkInstance | null;
    nextTime: number;
    transactionID: string | null;
    userId: string | null;
    setTransactionID: (transactionID: string | null) => void;
    setNextTime: (nextTime: number) => void;
    geolocationType: string;
    triggerGeolocation: Types.TriggerGeolocationCallback;
    requestPacket: () => Promise<void>;
    triggerTimestampRef: MutableRefObject<number | null>;
    intervalPings: boolean;
    inactiveDetection: boolean;
}) => {
    const isVisibleAndFocused = useRef(!document.hidden && document.hasFocus());

    useEffect(() => {
        if (!sdk || !userId || !inactiveDetection) {
            return;
        }

        const handler = () => isVisibleAndFocused.current = !document.hidden && document.hasFocus();

        document.addEventListener('visibilitychange', handler);
        window.addEventListener('focus', handler);

        return () => {
            document.removeEventListener('visibilitychange', handler);
            window.removeEventListener('focus', handler);
        };
    }, [isVisibleAndFocused, userId, sdk, inactiveDetection]);

    useEffect(() => {
        if (!sdk || !userId) {
            return;
        }

        if (!transactionID) {
            return;
        }

        const timeout = setTimeout(() => {
            if (inactiveDetection && !isVisibleAndFocused.current && !htcmdService.isWrapperEnabled) {
                const handler = once(() => triggerGeolocation({ reason: constants.GEOLOCATION_REASON.FOREGROUND_CHECK }));

                document.addEventListener('visibilitychange', handler, { once: true });
                window.addEventListener('focus', handler, { once: true });
                return;
            }

            requestPacket();
        }, nextTime);

        return () => {
            // unset current timeout
            triggerTimestampRef.current = null;
            setTransactionID(null);
            setNextTime(0);
            clearTimeout(timeout);
        };
    }, [
        sdk,
        nextTime,
        transactionID,
        userId,
        triggerGeolocation,
        setTransactionID,
        setNextTime,
        requestPacket,
        triggerTimestampRef,
        inactiveDetection
    ]);

    /**
     * Each 20 seconds make a request to IMS in order to trigger IP change, if IP of the player has changed.
     */
    useEffect(() => {
        if (!sdk || !userId || !transactionID || !nextTime || !intervalPings) {
            return;
        }

        const interval = setInterval(() => {
            validateGeolocation(null, null, null, null, null, geolocationType);
        }, 20000);

        return () => {
            clearInterval(interval);
        };
    }, [
        sdk,
        userId,
        transactionID,
        nextTime,
        geolocationType,
        intervalPings
    ]);
};
